import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { 
  Users, 
  Crown, 
  AlertTriangle, 
  IndianRupee, 
  MessageCircle, 
  Heart, 
  ShieldCheck, 
  BadgeCheck, 
  Sliders, 
  CreditCard, 
  MessageSquare,
  UserPlus,
  ArrowUpRight,
  ArrowDownRight,
  Camera,
  UserCheck,
  User
} from "lucide-react";
import { toast } from "sonner";

// Helper function for user-friendly relative time text
function formatRelativeTime(dateStr) {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHrs = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHrs / 24);

    if (diffSec < 60) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays === 1) return "yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch (e) {
    return "—";
  }
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/analytics")
    ])
      .then(([{ data: statsData }, { data: analyticsData }]) => {
        setStats(statsData);
        setAnalytics(analyticsData);
      })
      .catch((e) => {
        toast.error(formatError(e));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  // Trend logic for today's registrations compared to yesterday
  const getRegistrationTrend = () => {
    if (!stats) return null;
    const today = stats.registrations_today ?? 0;
    const yesterday = stats.registrations_yesterday ?? 0;
    const diff = today - yesterday;

    if (diff > 0) {
      return (
        <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full mt-2">
          <ArrowUpRight className="w-3.5 h-3.5" />
          +{diff} vs yesterday
        </span>
      );
    } else if (diff < 0) {
      return (
        <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded-full mt-2">
          <ArrowDownRight className="w-3.5 h-3.5" />
          {diff} vs yesterday
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded-full mt-2">
          Same as yesterday
        </span>
      );
    }
  };

  // Activity config mapper
  const getActivityConfig = (type) => {
    switch (type) {
      case "registration":
        return {
          bg: "bg-emerald-50",
          text: "text-emerald-600",
          border: "border-emerald-100",
          icon: UserPlus,
          link: (id) => `/admin/users?q=${encodeURIComponent(id || "")}`
        };
      case "payment":
        return {
          bg: "bg-blue-50",
          text: "text-blue-600",
          border: "border-blue-100",
          icon: IndianRupee,
          link: () => "/admin/settings/payments"
        };
      case "verification":
        return {
          bg: "bg-teal-50",
          text: "text-teal-600",
          border: "border-teal-100",
          icon: ShieldCheck,
          link: () => "/admin/verifications"
        };
      case "report":
        return {
          bg: "bg-amber-50",
          text: "text-amber-600",
          border: "border-amber-100",
          icon: AlertTriangle,
          link: () => "/admin/reports"
        };
      default:
        return {
          bg: "bg-slate-50",
          text: "text-slate-600",
          border: "border-slate-100",
          icon: Users,
          link: () => "/admin/users"
        };
    }
  };

  const maxDistrictCount = Math.max(
    ...(analytics?.district_data || []).map((d) => d.count),
    1
  );

  return (
    <div className="pb-10">
      <PageHeader title="Dashboard" subtitle="Platform metrics at a glance" />
      
      {/* 4 KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
        
        {/* Total Users */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 transition-all hover:border-[#6B1A24]/40 hover:shadow-md h-full flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Users</span>
              <Users className="w-4 h-4 text-[#6B1A24]" />
            </div>
            <div 
              className="text-3xl font-heading font-bold text-[#2A0A0E] mt-3" 
              data-testid="stat-total-users"
            >
              {loading ? "..." : (stats?.users_total ?? 0)}
            </div>
            {!loading && (stats?.users_male !== undefined || stats?.users_female !== undefined) && (
              <div className="flex gap-3 mt-3 text-xs text-slate-600 border-t border-slate-100 pt-2 font-medium">
                <Link to="/admin/users?gender=male" className="hover:underline flex items-center gap-1">
                  👨 Male: <strong className="text-blue-600 font-bold">{stats?.users_male ?? 0}</strong>
                </Link>
                <span>•</span>
                <Link to="/admin/users?gender=female" className="hover:underline flex items-center gap-1">
                  👩 Female: <strong className="text-rose-600 font-bold">{stats?.users_female ?? 0}</strong>
                </Link>
              </div>
            )}
          </div>
          <div className="mt-3 flex justify-between items-center text-xs text-slate-500">
            <span>Active user directory</span>
            <Link to="/admin/users" className="font-semibold text-[#6B1A24] hover:underline flex items-center gap-0.5">
              Manage Users <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {/* Today's Registrations */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 h-full flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Today's Registrations</span>
              <UserPlus className="w-4 h-4 text-[#6B1A24]" />
            </div>
            <div 
              className="text-3xl font-heading font-bold text-[#2A0A0E] mt-3" 
              data-testid="stat-today-s-registrations"
            >
              {loading ? "..." : (stats?.registrations_today ?? 0)}
            </div>
          </div>
          <div className="flex items-center">{loading ? null : getRegistrationTrend()}</div>
        </div>

        {/* Photo Queue */}
        <Link to="/admin/users?status=unverified_photos">
          <div className={`bg-white rounded-lg border shadow-sm p-5 transition-all hover:shadow-md h-full flex flex-col justify-between ${!loading && stats?.photo_queue > 0 ? "border-amber-300 ring-1 ring-amber-200 hover:border-amber-400 bg-amber-50/5" : "border-slate-200 hover:border-[#6B1A24]/40"}`}>
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Photo Queue</span>
                <Camera className={`w-4 h-4 ${!loading && stats?.photo_queue > 0 ? "text-amber-500 animate-pulse" : "text-[#6B1A24]"}`} />
              </div>
              <div 
                className={`text-3xl font-heading font-bold mt-3 ${!loading && stats?.photo_queue > 0 ? "text-amber-700" : "text-[#2A0A0E]"}`} 
                data-testid="stat-photo-queue"
              >
                {loading ? "..." : (stats?.photo_queue ?? 0)}
              </div>
            </div>
            <span className="text-xs text-slate-500 mt-2 block">Pending verification profiles</span>
          </div>
        </Link>

        {/* Monthly Revenue */}
        <Link to="/admin/settings/payments">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 transition-all hover:border-[#6B1A24]/40 hover:shadow-md h-full flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Monthly Revenue</span>
                <IndianRupee className="w-4 h-4 text-[#6B1A24]" />
              </div>
              <div 
                className="text-3xl font-heading font-bold text-[#2A0A0E] mt-3" 
                data-testid="stat-monthly-revenue"
              >
                {loading ? "..." : `₹${(stats?.monthly_revenue ?? 0).toLocaleString("en-IN")}`}
              </div>
            </div>
            <span className="text-xs text-slate-500 mt-2 block">Current billing cycle total</span>
          </div>
        </Link>
      </div>

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8 text-left">
        
        {/* Left 2 Columns: District Chart and Top Referrers */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          
          {/* District Bar Chart */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <h3 className="text-base font-heading font-semibold text-[#2A0A0E] mb-5 flex items-center gap-2">
              <span className="w-1.5 h-4 bg-[#6B1A24] rounded-full inline-block"></span>
              District Distribution
            </h3>

            {loading ? (
              <div className="py-20 text-center text-slate-400 text-sm">Loading district statistics...</div>
            ) : !analytics?.district_data || analytics.district_data.length === 0 ? (
              <div className="py-20 text-center text-slate-400 text-sm">No district distribution data available.</div>
            ) : (
              <div className="space-y-4">
                {analytics.district_data.map((item) => {
                  const pct = Math.max(2, (item.count / maxDistrictCount) * 100);
                  return (
                    <div key={item.district} className="flex items-center gap-4">
                      {/* Name */}
                      <div className="w-28 text-sm font-medium text-slate-700 truncate text-left" title={item.district}>
                        {item.district}
                      </div>
                      {/* Bar (Pure CSS/SVG Horizontal Bar) */}
                      <div className="flex-1 bg-slate-100 h-3 rounded-full overflow-hidden relative">
                        <div 
                          className="h-full bg-[#6B1A24] rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${pct}%` }}
                        ></div>
                      </div>
                      {/* Count */}
                      <div className="w-10 text-right text-xs font-bold text-slate-500">
                        {item.count}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top Referrers */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <h3 className="text-base font-heading font-semibold text-[#2A0A0E] mb-5 flex items-center gap-2">
              <span className="w-1.5 h-4 bg-[#6B1A24] rounded-full inline-block"></span>
              Top Referrers
            </h3>

            {loading ? (
              <div className="py-20 text-center text-slate-400 text-sm">Loading referrer leaderboards...</div>
            ) : !analytics?.top_referrers || analytics.top_referrers.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">No successful referrers yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/50">
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">District</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Referrals</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.top_referrers.map((ref) => (
                      <tr key={ref.user_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <Link 
                            to={`/admin/users?q=${encodeURIComponent(ref.user_id)}`}
                            className="flex items-center gap-3 group"
                          >
                            {ref.photo ? (
                              <img 
                                src={ref.photo} 
                                alt={ref.name} 
                                className="w-8 h-8 rounded-full object-cover border border-slate-100 shrink-0"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-[#6B1A24]/10 flex items-center justify-center shrink-0">
                                <User className="w-4 h-4 text-[#6B1A24]" />
                              </div>
                            )}
                            <span className="text-sm font-semibold text-slate-800 group-hover:text-[#6B1A24] transition-colors truncate max-w-[180px]">
                              {ref.name}
                            </span>
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">{ref.district}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="inline-block bg-[#6B1A24]/5 text-[#6B1A24] font-bold text-xs px-2.5 py-1 rounded-full border border-[#6B1A24]/10">
                            {ref.referral_count}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right 1 Column: Recent Activity Feed */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 flex flex-col h-full">
          <h3 className="text-base font-heading font-semibold text-[#2A0A0E] mb-5 flex items-center gap-2 shrink-0">
            <span className="w-1.5 h-4 bg-[#6B1A24] rounded-full inline-block"></span>
            Recent Activity
          </h3>

          {loading ? (
            <div className="py-20 text-center text-slate-400 text-sm flex-1 flex items-center justify-center">Loading activity feed...</div>
          ) : !analytics?.activity || analytics.activity.length === 0 ? (
            <div className="py-20 text-center text-slate-400 text-sm flex-1 flex items-center justify-center">No recent activities.</div>
          ) : (
            <div className="flex-1 overflow-y-auto pr-1 space-y-4 max-h-[750px]">
              {analytics.activity.map((act, index) => {
                const cfg = getActivityConfig(act.type);
                const IconComponent = cfg.icon;
                return (
                  <div key={index} className="flex gap-3 text-left">
                    {/* Icon Badge */}
                    <div className={`w-8 h-8 rounded-full border shrink-0 flex items-center justify-center ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                      <IconComponent className="w-4 h-4" />
                    </div>
                    {/* Description */}
                    <div className="flex-1 min-w-0 font-sans">
                      <div className="text-xs font-semibold text-slate-800">
                        {act.label}
                      </div>
                      {act.detail && (
                        <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                          {act.detail}
                        </div>
                      )}
                      <div className="text-[9px] text-slate-400 mt-1 flex items-center gap-1">
                        <span>{formatRelativeTime(act.at)}</span>
                        {act.user_id && (
                          <>
                            <span>•</span>
                            <Link 
                              to={cfg.link(act.user_id)} 
                              className="text-[#6B1A24] hover:underline"
                            >
                              Manage
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quick Settings Access */}
      <div className="mt-10">
        <h2 className="text-xl font-heading font-semibold text-[#2A0A0E] mb-4 text-left">Quick Settings Controls</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link to="/admin/settings/core" className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 hover:border-[#6B1A24]/40 flex items-center gap-3 transition-colors text-left" data-testid="quick-settings-core">
            <div className="w-10 h-10 rounded-full bg-[#6B1A24]/10 flex items-center justify-center shrink-0">
              <Sliders className="w-5 h-5 text-[#6B1A24]" />
            </div>
            <div>
              <div className="font-semibold text-sm text-[#2A0A0E]">Core Features</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Toggle authentication options & referrals</div>
            </div>
          </Link>
          <Link to="/admin/settings/payments" className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 hover:border-[#6B1A24]/40 flex items-center gap-3 transition-colors text-left" data-testid="quick-settings-payments">
            <div className="w-10 h-10 rounded-full bg-[#6B1A24]/10 flex items-center justify-center shrink-0">
              <CreditCard className="w-5 h-5 text-[#6B1A24]" />
            </div>
            <div>
              <div className="font-semibold text-sm text-[#2A0A0E]">Payment Settings</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Razorpay keys & subscription packages</div>
            </div>
          </Link>
          <Link to="/admin/settings/sms" className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 hover:border-[#6B1A24]/40 flex items-center gap-3 transition-colors text-left" data-testid="quick-settings-sms">
            <div className="w-10 h-10 rounded-full bg-[#6B1A24]/10 flex items-center justify-center shrink-0">
              <MessageSquare className="w-5 h-5 text-[#6B1A24]" />
            </div>
            <div>
              <div className="font-semibold text-sm text-[#2A0A0E]">SMS Providers</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Configure SMS gateway alerts</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
